import { useEffect, useId, useMemo, useState, type KeyboardEvent } from 'react'
import { useBackend } from '../../lib/session'
import type { CollectionSummary } from '../../lib/types'
import { errorText } from '../ui'
import { CollectionTile, thoughtCount } from '../CollectionLinkCard'

interface Props {
  onPick: (collection: CollectionSummary) => void
  onCancel: () => void
}

/**
 * The picker behind the + menu's Collection: every one of your collections
 * (as the publish sheet lists them), filtered as you type. Private ones can
 * be picked, but readers won't see the card until the collection is public.
 */
export function CollectionPicker({ onPick, onCancel }: Props) {
  const backend = useBackend()
  const listId = useId()
  const [query, setQuery] = useState('')
  const [all, setAll] = useState<CollectionSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState(0)

  useEffect(() => {
    let alive = true
    backend
      .listCollections()
      .then((r) => alive && setAll(r))
      .catch((e: unknown) => alive && setError(errorText(e)))
    return () => {
      alive = false
    }
  }, [backend])

  const items = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (all ?? []).filter((c) => !q || c.name.toLowerCase().includes(q))
  }, [all, query])

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    const n = items.length
    if (e.key === 'ArrowDown' && n) {
      e.preventDefault()
      setActive((a) => (a + 1) % n)
    } else if (e.key === 'ArrowUp' && n) {
      e.preventDefault()
      setActive((a) => (a - 1 + n) % n)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (items[active]) onPick(items[active])
    }
  }

  return (
    <div
      data-keep-selection
      role="dialog"
      aria-label="Link a collection"
      onKeyDown={(e) => {
        e.stopPropagation()
        if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      }}
      onClick={(e) => e.stopPropagation()}
      className="relative z-20 mb-[22px] flex w-full max-w-[520px] flex-col gap-1.5 rounded-2xl border border-border/50 bg-card p-2 font-sans text-foreground shadow-[var(--shadow-pop)]"
    >
      <div className="flex items-center justify-between gap-3 px-2.5 pt-1">
        <span className="min-w-0 truncate text-[10.5px] font-semibold tracking-[0.06em] text-muted-foreground">COLLECTION · A CARD FOR READERS TO EXPLORE</span>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-full text-[16px] leading-none text-muted-foreground hover:bg-foreground/5"
        >
          ×
        </button>
      </div>
      <input
        autoFocus
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setActive(0)
        }}
        onKeyDown={onSearchKey}
        placeholder="Find a collection"
        aria-label="Find a collection"
        aria-controls={listId}
        className="mx-1 rounded-lg border border-border/60 bg-canvas/60 px-3 py-2 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-accent"
      />
      <div id={listId} role="listbox" aria-label="Your collections" className="flex max-h-[300px] flex-col gap-0.5 overflow-y-auto">
        {error ? (
          <p className="m-0 px-2.5 py-3 text-[13px] text-muted-foreground">{error}</p>
        ) : all === null ? (
          <p className="m-0 px-2.5 py-3 text-[13px] text-muted-foreground">Loading collections…</p>
        ) : items.length === 0 ? (
          <p className="m-0 px-2.5 py-3 text-[13px] text-muted-foreground">{query ? 'No collection by that name.' : 'You have no collections yet.'}</p>
        ) : (
          items.map((c, i) => (
            <div
              key={c.id}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => onPick(c)}
              className={`flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 ${i === active ? 'bg-accent/15' : 'hover:bg-foreground/4'}`}
            >
              <CollectionTile color={c.cover_color} cover={c.cover_url} size={28} />
              <span className="min-w-0 flex-1 truncate font-display text-[14px] text-foreground">{c.name}</span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{c.is_public ? thoughtCount(c.thought_count) : 'Private'}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
